import React from 'react';
import styled, { css, keyframes } from 'styled-components';

/**
 * Avatar for an AI agent (iMaven's `template|…` advisors and published `agent|…` storefronts): the brand
 * orb — three soft blobs — behind a white spark, with an "AI" tag, so a conversation with an AI agent is
 * recognisable at a glance instead of showing the generic person icon (owner feedback 2026-10-20).
 * Static by default; the blobs drift slowly on hover of the avatar or of a parent marked `.ai-hover`.
 */

/** True for the ids of AI agents: iMaven's advisors (`template|…`) and adviser storefronts (`agent|…`). */
export const isAiAgentId = (id?: string | null): boolean => /^(template|agent)\|/.test(String(id || ''));

const driftA = keyframes`0%,100%{transform:translate(-14%,-10%) scale(.62)}50%{transform:translate(10%,-16%) scale(.7)}`;
const driftB = keyframes`0%,100%{transform:translate(16%,10%) scale(.6)}50%{transform:translate(-10%,14%) scale(.68)}`;
const driftC = keyframes`0%,100%{transform:translate(-6%,18%) scale(.56)}50%{transform:translate(12%,4%) scale(.64)}`;

const Wrap = styled.span<{ $px: number }>`
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: ${(p) => p.$px}px;
  height: ${(p) => p.$px}px;
`;

const Tile = styled.span<{ $radius: number }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: ${(p) => p.$radius}px;
  background: linear-gradient(140deg, #f3f6ff 0%, #f1ebff 100%);
  border: 1px solid #e2e8fb;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .8), 0 2px 8px rgba(51, 92, 244, .08);
`;

const blob = (bg: string, start: string, anim: ReturnType<typeof keyframes>) => css`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  mix-blend-mode: multiply;
  opacity: .8;
  filter: blur(1px);
  background: ${bg};
  transform: ${start};
  animation: ${anim} 5s ease-in-out infinite;
  animation-play-state: paused;
  ${Wrap}:hover &, .ai-hover:hover & { animation-play-state: running; }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const BlobA = styled.i`${blob('radial-gradient(circle at 35% 35%, #7d97ff, #335cf4 72%)', 'translate(-14%,-10%) scale(.62)', driftA)}`;
const BlobB = styled.i`${blob('radial-gradient(circle at 60% 40%, #bc9fff, #885de8 72%)', 'translate(16%,10%) scale(.6)', driftB)}`;
const BlobC = styled.i`${blob('radial-gradient(circle at 50% 60%, #8adcf7, #29a9dd 72%)', 'translate(-6%,18%) scale(.56)', driftC)}`;

const Spark = styled.svg`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 46%;
  height: 46%;
  transform: translate(-50%, -50%);
  filter: drop-shadow(0 1px 2px rgba(30, 50, 140, .35));
`;

const Tag = styled.span<{ $px: number }>`
  position: absolute;
  right: -5px;
  bottom: -4px;
  padding: 1px 5px;
  border-radius: 999px;
  border: 2px solid #fff;
  background: linear-gradient(110deg, #335cf4, #885de8);
  color: #fff;
  font-size: ${(p) => (p.$px >= 56 ? 10 : 8.5)}px;
  font-weight: 700;
  letter-spacing: .3px;
  line-height: 1.3;
`;

interface AiAgentAvatarProps {
  size?: number;
  radius?: number;
  label?: string;
  className?: string;
}

export const AiAgentAvatar: React.FC<AiAgentAvatarProps> = ({ size = 40, radius, label = 'AI agent', className }) => (
  <Wrap $px={size} className={className} role="img" aria-label={label}>
    <Tile $radius={radius ?? Math.round(size / 4)}>
      <BlobA /><BlobB /><BlobC />
      <Spark viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#fff" d="M12 1.5c.5 4.9 2.6 7.6 9.5 10.5-6.9 2.9-9 5.6-9.5 10.5-.5-4.9-2.6-7.6-9.5-10.5C9.4 9.1 11.5 6.4 12 1.5z" />
      </Spark>
    </Tile>
    <Tag $px={size} aria-hidden="true">AI</Tag>
  </Wrap>
);

export default AiAgentAvatar;
